import React from 'react';
import { maximumOf } from 'components/utils/minMax';

// Shared rendering helpers for a manufacturing-line listing's cards, so the main listing
// (ManufacturingLineLists) and the standard-lines fallback shown when the owner's own lines come up
// empty (StandardManufacturingLinesFallback) draw a card the SAME way -- one source of truth for the
// stars, the column de-duplication, the sub_section badges and the common machine scale.

// The star rating for a score: the BEST (lowest, cheapest) score is always 5 stars, and every other score is
// PROPORTIONAL to it -- a plan that costs twice the best earns half the stars (best/score * 5). Not a position
// in the on-screen range: a plan only a little dearer than the best now reads a little under 5, rather than
// being stretched down to 1 just for being the worst on screen. The same shape the solution listing uses
// (sortSolutionsByScoreAndDrawStars). null for a plan with no score; 5 when this IS the best (or the only one).
export const starRatingForScore = (score, bestScore) => {
  if (score == null) { return null; }
  if (!(bestScore > 0) || !(score > 0)) { return 5; }

  return Math.round(Math.max(1, Math.min(5, (bestScore / score) * 5)) * 10) / 10; // best = 5, never below 1
};

// A star row for a rating -- full stars, a half star, empty stars, then the numeric rating. The same rendering
// the legacy solution listing draws, in React.
export const Stars = ({ rating }) => {
  if (rating == null) { return null; }

  const full = Math.floor(rating);
  const remainder = rating - full;
  const hasHalf = remainder >= 0.25 && remainder < 0.75;
  const empty = Math.max(0, 5 - full - (hasHalf ? 1 : 0));

  return (
    <span style={{ color: '#ffc107', fontSize: '14px' }}>
      {'★'.repeat(full)}
      {hasHalf ? (
        <span style={{ position: 'relative', display: 'inline-block' }}>
          <span style={{ color: '#ddd' }}>★</span>
          <span style={{ position: 'absolute', left: 0, width: '50%', overflow: 'hidden', color: '#ffc107' }}>★</span>
        </span>
      ) : null}
      {'☆'.repeat(empty)}
      <span style={{ marginLeft: '4px', color: '#666', fontSize: '13px' }}>{rating.toFixed(1)}</span>
    </span>
  );
};

// Which columns each manufacturing line in a list draws, so a machine two of the list's lines share is drawn
// once and the second line badges onto the first rather than repeating an identical machine pair.
//
// machineId -> the INDEX of the manufacturing line that drew it. Which one drew it matters, not merely that one
// did: a manufacturing line with no box left is badged onto the line that draws its machines, and only that line
// can honestly say "this part runs on these machines".
export const columnVisibilityFor = (manufacturingLines) => {
  const drawnBy = new Map();

  return manufacturingLines.map((manufacturingLine, index) => {
    const machineIds = [
      manufacturingLine.printing_machine_id,
      manufacturingLine.die_cutting_machine_id,
      manufacturingLine.printing_and_die_cutting_machine_id
    ];
    const alreadyDrawn = (machineId) => machineId != null && drawnBy.has(machineId);
    const ownMachineIds = machineIds.filter((machineId) => machineId != null);

    // The manufacturing lines that drew this one's machines -- undefined for a machine nobody drew yet.
    const owners = new Set(ownMachineIds.map((machineId) => drawnBy.get(machineId)));
    // Droppable ONLY when every machine came from ONE earlier manufacturing line. That line's machines ARE
    // this one's, so it can carry the badge. Machines drawn by two DIFFERENT lines have no honest target
    // (the part runs on a pair no single row shows), and a line nobody drew from still has a box of its
    // own -- both keep their row. A machine-less line has no owners at all, so it keeps its row too.
    const drawnByOneManufacturingLine = owners.size === 1 && !owners.has(undefined);

    const visibility = {
      hidePrintingColumn: alreadyDrawn(manufacturingLine.printing_machine_id),
      hideDieCuttingColumn: alreadyDrawn(manufacturingLine.die_cutting_machine_id),
      hideCombinedColumn: alreadyDrawn(manufacturingLine.printing_and_die_cutting_machine_id),
      drawsNothing: drawnByOneManufacturingLine,
      // The manufacturing line whose badges this one joins, or null when it keeps its own row.
      badgeOntoIndex: drawnByOneManufacturingLine ? [...owners][0] : null
    };

    // FIRST drawer wins: it is the row the machine is actually drawn on.
    machineIds.forEach((machineId) => {
      if (machineId != null && !drawnBy.has(machineId)) { drawnBy.set(machineId, index); }
    });
    return visibility;
  });
};

// This manufacturing line's own part, plus the parts of any whose machines IT draws. Badging onto the owner
// rather than the first row is what keeps the card honest once a list has more than two lines: with three, the
// machines of the third can belong to the second, and saying it runs on the first's pair would name machines it
// never touches.
export const sectionLabelsFor = (list, columnVisibility, index) => [
  list.manufacturing_lines[index].sub_section_label,
  ...list.manufacturing_lines
    .filter((_manufacturingLine, otherIndex) => columnVisibility[otherIndex].badgeOntoIndex === index)
    .map((manufacturingLine) => manufacturingLine.sub_section_label)
];

// The largest machine sheet dimension across every manufacturing line in the given lists -- the common scale
// that makes the per-machine rectangles comparable to each other (a bigger machine renders bigger), including
// across two lines stacked in one card. The same scale the legacy listing computes. At least 1, so an empty set
// never divides the card renderer by zero.
export const maxMachineDimensionOf = (lists) => {
  const allManufacturingLines = lists.flatMap((list) => list.manufacturing_lines);
  return Math.max(1, maximumOf(allManufacturingLines.flatMap((manufacturingLine) => [
    manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm,
    manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm
  ].map((value) => parseFloat(value) || 0))));
};
